import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { getPharmacyMedicinePurchaseHistoryApi } from "../../../services/pharmacyPurchase.services";
import { formatRupees } from "../common/money";
import { gstPercent } from "../common/gstMath";
import { toId } from "../common/ids";
import HistoryStrip, {
  HistoryNote,
  HistoryLoading,
  rateDelta,
} from "../common/HistoryStrip";

/*
 * Purchase History, opened under the line being entered.
 *
 * The same strip the counter reads on a sale, asking the other side of the
 * question: not what this patient last paid for the medicine, but what the
 * pharmacy last paid for it - the entry it came in on, from which vendor, at
 * what rate, on which batch, against what MRP. It is the figure a purchase
 * rate being typed now wants judging against, and the one nobody had without
 * going and opening last month's entries.
 *
 * The entry being typed is left out of its own history: a line is not
 * evidence about itself.
 */

/*
 * The counter's twelve tracks, to the pixel - the same widths, the same
 * alignments, the same heading over the panel. A purchaser reading this strip
 * and a pharmacist reading the one on a sale are reading the same thing about
 * the same medicine, so the two are not told apart by their shape.
 *
 * Four labels name what this side actually has: an entry rather than a bill,
 * the quantity and the free quantity a vendor ships rather than the strips and
 * tablets a patient is given, the vendor it came from rather than a remark
 * against a sale.
 */
const COLUMNS = [
  { key: "entry", label: "Entry No", width: "80px" },
  { key: "date", label: "Date", width: "84px", tnum: false },
  { key: "qty", label: "Qty", width: "44px", align: "ctr" },
  { key: "free", label: "Free", width: "36px", align: "ctr" },
  { key: "batch", label: "Batch", width: "minmax(70px, 1fr)" },
  { key: "mrp", label: "MRP", width: "48px", align: "end" },
  { key: "rate", label: "Rate", width: "60px", align: "end" },
  { key: "disc", label: "Disc", width: "40px", align: "end" },
  { key: "net", label: "Net Rate", width: "66px", align: "end" },
  { key: "gst", label: "GST", width: "36px", align: "end" },
  { key: "expiry", label: "Expiry", width: "82px", tnum: false },
  { key: "vendor", label: "Vendor", width: "minmax(72px, 1.2fr)", tnum: false },
];

/** The first of several names a figure might arrive under. */
const pick = (row, ...names) => {
  for (const name of names) {
    const value = row?.[name];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * What a purchase actually cost per unit.
 *
 * `discount` is rupees off the whole line - the same unit the entry screen
 * writes it in - so it comes off per unit before one purchase is compared
 * with another. Free quantity is deliberately not counted into it: the
 * discount a vendor gives in goods is a different thing from the one they
 * give in money, and folding them together hides which was which.
 */
const netRate = (row, rate, qty) => {
  const stated = num(pick(row, "net_rate", "netRate"));
  if (stated !== null) return stated;

  const discount = num(row?.discount) ?? 0;
  if (!qty || !discount) return rate ?? 0;
  return Math.max((rate ?? 0) - discount / qty, 0);
};

/*
 * One row of the strip, from whatever the endpoint called its fields.
 *
 * Read rather than specified here, the same way the sale side reads
 * `pharmacy/bills/medicine-history/`: each column is looked for under the
 * names it could plausibly arrive under and prints an em dash where it did
 * not arrive at all. A column the server starts sending fills itself in; one
 * it never sends says so rather than printing a nought that reads as a real
 * figure.
 */
const readRow = (row, index) => {
  const rate = num(pick(row, "purchase_rate", "rate"));
  const qty = num(pick(row, "qty", "quantity"));
  const discountAmount = num(row?.discount);

  /* Quoted as a percentage because that is how a vendor quotes it, and the
     entry stores it in rupees off the line. A percentage of the server's own
     is believed instead. */
  const statedPct = num(pick(row, "discount_percentage", "discount_percent", "disc"));
  const gross = (rate ?? 0) * (qty ?? 0);
  const disc =
    statedPct !== null
      ? statedPct
      : discountAmount && gross
        ? Math.round((discountAmount / gross) * 1000) / 10
        : null;

  return {
    key: `${pick(row, "entry_id", "purchase_entry", "id") ?? "row"}-${
      pick(row, "batch_number", "batch") ?? ""
    }-${index}`,
    entryId: pick(row, "entry_id", "purchase_entry", "entry", "id"),
    entryNumber: pick(row, "entry_number", "purchase_entry_number", "entryNumber"),
    vendor: pick(row, "vendor_name", "vendor", "supplier_name"),
    date: (() => {
      const value = pick(row, "date", "purchase_date");
      return value ? dayjs(value) : null;
    })(),
    qty,
    free: num(pick(row, "free_qty", "free")),
    batch: pick(row, "batch_number", "batch", "batchNumber"),
    mrp: num(pick(row, "mrp", "batch_mrp")),
    rate,
    disc,
    net: netRate(row, rate, qty),
    /* One figure where the endpoint sends one, worked out from the heads
       where it sends those instead - on the module's own rule, so a row
       carrying both spellings is not taxed twice over. */
    gst: num(pick(row, "gst", "gst_percentage", "gst_percent")) ?? gstPercent(row),
    expiry: pick(row, "expiry", "expiry_date", "batch_expiry"),
  };
};

const money = (value) => (value === null ? null : formatRupees(value));

const InlinePurchaseHistory = ({ medicineId, medicineName, currentRate, entryId }) => {
  const medicineKey = toId(medicineId);
  const entryKey = toId(entryId);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  /*
   * Which medicine the rows on screen belong to.
   *
   * The ledger is re-read whenever its medicine changes - and also whenever
   * the entry's own id does, which happens the moment the header is saved on
   * an entry that did not exist before. Tearing the table down for a spinner
   * on that second kind of read collapses the panel to a line and opens it
   * again a moment later. A read about the same medicine leaves what is on
   * screen where it is until the new answer arrives.
   */
  const [shownFor, setShownFor] = useState("");

  /* Opened on one medicine, read for another: the ledger folds again rather
     than carrying the last line's "View all 9 purchases" over to a new one. */
  useEffect(() => {
    setOpen(false);
  }, [medicineKey]);

  useEffect(() => {
    if (!medicineKey) {
      setRows([]);
      setFailed(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setFailed(false);

    const params = new URLSearchParams({ medicine: String(medicineKey) });

    getPharmacyMedicinePurchaseHistoryApi(params)
      .then((res) => {
        if (!live) return;
        const data = res?.data;
        const history = Array.isArray(data) ? data : data?.results || [];
        setRows(
          history
            .map(readRow)
            /* The entry being typed is not evidence about itself. */
            .filter((row) => !entryKey || toId(row.entryId) !== entryKey)
            /* Newest first, whatever order the server sends them in. */
            .sort((a, b) => (b.date?.valueOf() ?? 0) - (a.date?.valueOf() ?? 0))
        );
        setShownFor(String(medicineKey));
      })
      .catch(() => {
        if (!live) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [medicineKey, entryKey]);

  if (!medicineKey) return null;

  /* Showing another medicine's ledger, or nothing at all, while this one is
     being fetched would be a lie - that is what the spinner is for. */
  const stale = shownFor !== String(medicineKey);
  if (loading && (stale || !rows.length)) return <HistoryLoading />;

  if (failed) {
    return (
      <HistoryNote>Could not load the price history for this medicine.</HistoryNote>
    );
  }

  if (!rows.length) {
    return (
      <HistoryNote>
        {/*
          * The ledger is built from entries the stock actually came in on, so
          * a medicine sitting only on drafts has nothing to report yet - which
          * is worth saying, because it is not the same as never having been
          * bought.
          */}
        No earlier purchase of {medicineName || "this medicine"} on record.
        Entries appear here once they have been completed.
      </HistoryNote>
    );
  }

  const delta = rateDelta(currentRate, rows[0].net, {
    higher: (by) => `↑ Today ${formatRupees(by)} higher`,
    lower: (by) => `↓ Today ${formatRupees(by)} lower`,
    same: "Same rate today",
  });

  /* Folded, the panel is the last purchase alone - every column still there. */
  const shown = open ? rows : rows.slice(0, 1);

  return (
    <HistoryStrip
      subject={medicineName || undefined}
      delta={delta}
      toggle={
        rows.length > 1
          ? {
              open,
              onToggle: () => setOpen((was) => !was),
              label: open
                ? "Show last purchase only"
                : `View all ${rows.length} purchases`,
            }
          : null
      }
      columns={COLUMNS}
      rows={shown.map((entry) => ({
        key: entry.key,
        dim: ["mrp", "disc", "gst", "vendor"],
        strong: ["entry"],
        cells: {
          entry: entry.entryNumber,
          date: entry.date ? entry.date.format("DD MMM YYYY") : null,
          qty: entry.qty,
          free: entry.free || null,
          batch: entry.batch,
          mrp: money(entry.mrp),
          rate: money(entry.rate),
          disc: entry.disc ? `${entry.disc}%` : null,
          net: <b>{money(entry.net)}</b>,
          gst: entry.gst ? `${entry.gst}%` : null,
          expiry: entry.expiry ? dayjs(entry.expiry).format("DD MMM YYYY") : null,
          vendor: entry.vendor,
        },
      }))}
    />
  );
};

export default InlinePurchaseHistory;
